import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
// material
import { alpha, styled } from "@mui/material/styles";
import {
  Box,
  Stack,
  Avatar,
  Drawer,
  Tooltip,
  Typography,
  CardActionArea,
  Link,
  Paper,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
// hooks
import useCollapseDrawer from "../../hooks/useCollapseDrawer";
// components
import Logo from "../../components/Logo";
import Scrollbar from "../../components/Scrollbar";
import NavSection from "../../components/NavSection";
//
import { MHidden } from "../../components/@material-extend";
import { Block } from "../../components/Block";
import useAuth from "../../hooks/useAuth";
import sidebarConfig from "./SidebarConfig";
import { ASSETS_PATH } from "../../utils/apis";

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      duration: theme.transitions.duration.complex,
    }),
  },
}));

const AccountStyle = styled("div")(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12],
}));
const ListWrapperStyle = styled(Paper)(() => ({
  width: "100%",
  backgroundColor: "inherit",
  borderRadius: "0px",
}));

// ----------------------------------------------------------------------

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool,
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <Tooltip title="Collapse Menu">
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: "flex",
          cursor: "pointer",
          borderRadius: "50%",
          alignItems: "center",
          color: "text.primary",
          justifyContent: "center",
          border: "solid 1px currentColor",
          ...(collapseClick && {
            borderWidth: 2,
          }),
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: "currentColor",
            transition: (theme) => theme.transitions.create("all"),
            ...(collapseClick && {
              width: 0,
              height: 0,
            }),
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { user, switchAccount } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { accounts } = user;
  const photo =
    user && user.profile && user.profile.photo
      ? `${ASSETS_PATH.users}${user.profile.photo}`
      : "/static/placeholder.svg";

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  } = useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleAccountSwitch = async (uid) => {
    try {
      const { email } = accounts.filter((account) => account.email !== "")[0];
      enqueueSnackbar("Switching account...", { variant: "info" });
      setOpen(false);
      await switchAccount(uid, email);
      navigate("/");
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };
  const renderContent = (
    <Scrollbar
      sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: "center",
          }),
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box component={RouterLink} to="/" sx={{ display: "inline-flex" }}>
            <Logo disabledLink />
          </Box>

          <MHidden width="lgDown">
            {!isCollapse && (
              <IconCollapse
                onToggleCollapse={onToggleCollapse}
                collapseClick={collapseClick}
              />
            )}
          </MHidden>
        </Stack>

        {isCollapse ? (
          <Avatar
            alt={user.profile.fname}
            src={photo}
            sx={{ mx: "auto", mb: 2 }}
          />
        ) : (
          <AccountStyle>
            <Link
              underline="none"
              sx={{ cursor: "pointer" }}
              onClick={() => setOpen(!open)}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box sx={{ ml: 2, maxWidth: "80%" }}>
                  <Typography
                    noWrap
                    variant="subtitle2"
                    sx={{ color: "text.primary" }}
                  >
                    {`${user.profile.fname} ${user.profile.lname}`}
                  </Typography>
                  <Typography
                    noWrap
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    {(user &&
                      (user.company
                        ? user.company.name
                        : user.profile.email)) ||
                      (user && !user.profile.email && "JOINT ACCOUNT") ||
                      "" ||
                      ""}
                  </Typography>
                </Box>
                {accounts.length > 1 &&
                  ((open && <KeyboardArrowDownIcon />) || (
                    <Tooltip title="Switch Account">
                      <KeyboardArrowUpIcon />
                    </Tooltip>
                  ))}
              </Stack>
            </Link>
            {accounts.length > 1 && (
              <Block
                title={user && user.user ? `Accounts - ${accounts.length}` : ""}
                open={open}
              >
                <ListWrapperStyle>
                  <List component="nav">
                    {accounts.map((account) => (
                      <ListItemButton
                        onClick={() => handleAccountSwitch(account.uid)}
                        key={account.uid}
                      >
                        <ListItemText
                          primary={account.companyName || account.name}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </ListWrapperStyle>
              </Block>
            )}
          </AccountStyle>
        )}
      </Stack>

      <NavSection navConfig={sidebarConfig} isShow={!isCollapse} />
      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        displayPrint: "none",
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH,
        },
        ...(collapseClick && {
          position: "absolute",
        }),
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: "background.default",
              ...(isCollapse && {
                width: COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)", // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) =>
                  alpha(theme.palette.background.default, 0.88),
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
